import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { societyContactCreateSchema } from '../../../../forms/schemas/society_contact_create'
import { NBoardroomContacts } from '../../../../interfaces/services/boardroom-contacts.interfaces'
import { CreateUpdateContactForm } from '../CreateUpdateContactForm'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ISocietyContactWithFiles } from '../../../../interfaces/services/society-contacts.interface'
import { createContact, patchContact } from '../../../../api/society-contacts'
import {
  patchContact as patchBoardRoomContact,
  createContact as createBoardRoomContact,
} from '../../../../api/society-boardroom-contacts'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'

interface CreateUpdateContactModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  contact?: ISocietyContactWithFiles
  management?: boolean
}

export const CreateUpdateContactModal = ({
  contact,
  children,
  show,
  management,
  close,
}: CreateUpdateContactModalProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const updateMode = !!contact

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateContactModal.flashMessage.updateContactFailure'
      : 'createUpdateContactModal.flashMessage.createContactFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateContactModal.flashMessage.updateContactSuccess'
      : 'createUpdateContactModal.flashMessage.createContactSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutatePatchContact, isPending: isPatchingContact } =
    useMutation({
      mutationFn: patchContact,
      onError: () => {
        showErrorMessage()
      },
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
    })
  const {
    mutate: mutatePatchBoardRoomContact,
    isPending: isPatchingBoardContact,
  } = useMutation({
    mutationFn: patchBoardRoomContact,
    onError: () => {
      showErrorMessage()
    },
    onSuccess: () => {
      close()
      showSuccessMessage()
    },
  })
  const { mutate: mutateCreateContact, isPending: isCreatingContact } =
    useMutation({
      mutationFn: createContact,
      onError: () => {
        showErrorMessage()
      },
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
    })
  const {
    mutate: mutateCreateBoardRoomContact,
    isPending: isCreatingBoardContact,
  } = useMutation({
    mutationFn: createBoardRoomContact,
    onError: () => {
      showErrorMessage()
    },
    onSuccess: () => {
      close()
      showSuccessMessage()
    },
  })
  const { society } = useCurrentSociety()
  const onSubmit = async (
    data: InferType<typeof societyContactCreateSchema>
  ): Promise<void> => {
    const dataToSend = {
      ...data,
      website: data.website || '',
      email: data.email || '',
    }

    if (updateMode) {
      if (management) {
        mutatePatchBoardRoomContact({
          id: contact._id,
          body: dataToSend as NBoardroomContacts.NPatch.IRequestBody,
        })
      } else {
        mutatePatchContact({
          id: contact._id,
          body: dataToSend as NBoardroomContacts.NPatch.IRequestBody,
        })
      }
    } else if (management) {
      mutateCreateBoardRoomContact(
        dataToSend as NBoardroomContacts.NCreate.IRequestBody
      )
    } else {
      mutateCreateContact(dataToSend as NBoardroomContacts.NCreate.IRequestBody)
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading =
    isCreatingContact ||
    isCreatingBoardContact ||
    isPatchingContact ||
    isPatchingBoardContact

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateContactModal.editContactTitle'
                  : 'createUpdateContactModal.addNewContactTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {society && (
                <CreateUpdateContactForm
                  onError={onError}
                  society={society}
                  onSubmit={onSubmit}
                  onClose={close}
                  loading={loading}
                  contact={contact}
                  isBoardMember={management}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
